/**
 * MiniCart - Dropdown cart preview
 * Phase C4: Cart Icon & Mini Cart
 * 
 * Usage:
 * <MiniCart isOpen={showMiniCart} onClose={() => setShowMiniCart(false)} />
 */

import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { getCartItemImage } from '../utils/packageIcons';
import './MiniCart.css';

const MiniCart = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { cartItems, itemCount, totalPrice, removeItem, loading } = useCart();
  const miniCartRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (miniCartRef.current && !miniCartRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Group items by shop
  const itemsByShop = cartItems.reduce((acc, item) => {
    const shopId = item.shopId?._id || item.shopId;
    // For freelancers, show business name; for shops, show shop location name
    const shopName = item.shopId?.businessId?.name || item.shopId?.name || 'Unknown Shop';
    
    if (!acc[shopId]) {
      acc[shopId] = {
        shopName,
        items: []
      };
    }
    
    acc[shopId].items.push(item);
    return acc;
  }, {});

  const handleViewCart = () => {
    navigate('/cart');
    onClose();
  };

  const handleRemoveItem = async (itemId, e) => {
    e.stopPropagation();
    await removeItem(itemId);
  };

  const getItemName = (item) => {
    if (item.itemType === 'package') {
      return item.packageId?.name || 'Package';
    }
    return item.productId?.masterProductId?.name || 'Product';
  };

  if (!isOpen) return null;

  return (
    <div className="mini-cart-overlay">
      <div className="mini-cart-container" ref={miniCartRef}>
        <div className="mini-cart-header">
          <h3>Shopping Cart</h3>
          <button className="mini-cart-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="mini-cart-body">
          {itemCount === 0 ? (
            <div className="mini-cart-empty">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="64" 
                height="64" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="1.5"
              >
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              <p>Your cart is empty</p>
            </div>
          ) : (
            <>
              {Object.entries(itemsByShop).map(([shopId, { shopName, items }]) => (
                <div key={shopId} className="mini-cart-shop-group">
                  <div className="mini-cart-shop-header">
                    <span className="shop-icon">🏪</span>
                    <span className="shop-name">{shopName}</span>
                  </div>
                  
                  {items.slice(0, 3).map((item) => (
                    <div key={item._id} className="mini-cart-item">
                      <img 
                        src={getCartItemImage(item)} 
                        alt={getItemName(item)}
                      />
                      <div className="mini-cart-item-details">
                        <div className="mini-cart-item-name">{getItemName(item)}</div>
                        <div className="mini-cart-item-meta">
                          {item.itemType === 'package' ? (
                            <span className="item-type-badge package">Package</span>
                          ) : (
                            <span className="item-quantity">Qty: {item.quantity}</span>
                          )}
                          {item.packageOptions?.isGift && (
                            <span className="gift-badge">🎁 Gift</span>
                          )}
                        </div>
                        <div className="mini-cart-item-price">
                          ${(item.priceAtAdd * item.quantity).toFixed(2)}
                        </div>
                      </div>
                      <button 
                        className="mini-cart-item-remove"
                        onClick={(e) => handleRemoveItem(item._id, e)}
                        disabled={loading}
                        aria-label="Remove item"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  
                  {items.length > 3 && (
                    <div className="mini-cart-more-items">
                      +{items.length - 3} more item{items.length - 3 > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {itemCount > 0 && (
          <div className="mini-cart-footer">
            <div className="mini-cart-subtotal">
              <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
              <span className="subtotal-amount">${totalPrice.toFixed(2)}</span>
            </div>
            <button 
              className="mini-cart-view-cart-btn"
              onClick={handleViewCart}
            >
              View Full Cart
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniCart;
